import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ClientOverviewCard = ({ 
  client, 
  stats, 
  needsAttention, 
  viewMode, 
  onMessage, 
  onAssignWorkout, 
  onViewProgress 
}) => {
  const getAdherenceColor = (score) => {
    if (score >= 80) return 'text-green-500';
    if (score >= 60) return 'text-yellow-500';
    return 'text-red-500';
  };

  const getAdherenceBgColor = (score) => {
    if (score >= 80) return 'bg-green-500/10';
    if (score >= 60) return 'bg-yellow-500/10';
    return 'bg-red-500/10';
  };

  if (viewMode === 'list') {
    return (
      <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12, padding: '16px' }}>
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
          <div className="flex items-center gap-4 flex-1">
            <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'rgba(170,255,0,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <span style={{ fontWeight: 800, fontSize: '1.1rem', color: '#AAFF00', fontFamily: "'Inter Tight','Inter',sans-serif" }}>
                {client?.fullName?.charAt(0)?.toUpperCase()}
              </span>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 style={{ fontWeight: 700, color: '#fff', fontSize: '0.9rem' }}>{client?.fullName}</h3>
                {needsAttention && (
                  <span style={{ padding: '2px 8px', background: 'rgba(255,107,0,0.12)', color: '#FF6B00', fontSize: '0.65rem', borderRadius: 20, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Icon name="AlertCircle" size={11} />
                    Attention
                  </span>
                )}
              </div>
              <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.35)' }}>{client?.email}</p>
            </div>
          </div>

          <div className="flex items-center gap-6">
            <div className="text-center">
              <p style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Adherence</p>
              <p style={{ fontFamily: "'Inter Tight','Inter',sans-serif", fontWeight: 800, fontSize: '1.1rem', color: getAdherenceColor(stats?.adherenceScore), letterSpacing: '-0.02em' }}>
                {stats?.adherenceScore}%
              </p>
            </div>
            <div className="text-center">
              <p style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Completed</p>
              <p style={{ fontFamily: "'Inter Tight','Inter',sans-serif", fontWeight: 800, fontSize: '1.1rem', color: '#fff', letterSpacing: '-0.02em' }}>
                {stats?.completedWorkouts}/{stats?.totalWorkouts}
              </p>
            </div>
            <div className="text-center">
              <p style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Scheduled</p>
              <p style={{ fontFamily: "'Inter Tight','Inter',sans-serif", fontWeight: 800, fontSize: '1.1rem', color: '#00D4FF', letterSpacing: '-0.02em' }}>
                {stats?.scheduledWorkouts}
              </p>
            </div>
          </div>

          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onMessage}><Icon name="MessageCircle" size={16} /></Button>
            <Button variant="outline" size="sm" onClick={onAssignWorkout}><Icon name="Plus" size={16} /></Button>
            <Button variant="default" size="sm" onClick={onViewProgress}>View Progress</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 14, overflow: 'hidden' }}
      whileHover={{ y: -4, boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}
    >
      {needsAttention && (
        <div style={{ background: 'rgba(255,107,0,0.08)', borderBottom: '1px solid rgba(255,107,0,0.15)', padding: '8px 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Icon name="AlertCircle" size={15} style={{ color: '#FF6B00' }} />
          <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#FF6B00' }}>Needs Attention</span>
        </div>
      )}
      <div style={{ padding: '20px' }}>
        <div className="flex items-center gap-3 mb-4">
          <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'rgba(170,255,0,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 0 16px rgba(170,255,0,0.15)' }}>
            <span style={{ fontFamily: "'Inter Tight','Inter',sans-serif", fontWeight: 900, fontSize: '1.3rem', color: '#AAFF00' }}>
              {client?.fullName?.charAt(0)?.toUpperCase()}
            </span>
          </div>
          <div className="flex-1">
            <h3 style={{ fontWeight: 700, color: '#fff', fontSize: '0.95rem' }}>{client?.fullName}</h3>
            <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.35)' }}>{client?.email}</p>
          </div>
        </div>

        {/* Adherence Score — replaced bar with neon accent */}
        <div style={{ marginBottom: 16 }}>
          <div className="flex justify-between items-center mb-2">
            <span style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Adherence Score</span>
            <span style={{ fontFamily: "'Inter Tight','Inter',sans-serif", fontWeight: 800, fontSize: '1.1rem', color: getAdherenceColor(stats?.adherenceScore), letterSpacing: '-0.02em' }}>
              {stats?.adherenceScore}%
            </span>
          </div>
          <div style={{ width: '100%', background: 'rgba(255,255,255,0.06)', borderRadius: 4, height: 4 }}>
            <div style={{
              height: 4, borderRadius: 4,
              width: `${stats?.adherenceScore}%`,
              background: getAdherenceColor(stats?.adherenceScore) === 'text-green-500' ? '#AAFF00' : getAdherenceColor(stats?.adherenceScore) === 'text-yellow-500' ? '#FFD700' : '#ef4444',
              boxShadow: `0 0 8px ${getAdherenceColor(stats?.adherenceScore) === 'text-green-500' ? '#AAFF00' : '#FF6B00'}66`,
              transition: 'width 0.6s ease',
            }} />
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          {[
            { label: 'Total', value: stats?.totalWorkouts, color: 'rgba(255,255,255,0.8)' },
            { label: 'Completed', value: stats?.completedWorkouts, color: '#AAFF00' },
            { label: 'Scheduled', value: stats?.scheduledWorkouts, color: '#00D4FF' },
          ]?.map(s => (
            <div key={s?.label} style={{ textAlign: 'center', background: 'rgba(255,255,255,0.03)', borderRadius: 8, padding: '8px 4px' }}>
              <p style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>{s?.label}</p>
              <p style={{ fontFamily: "'Inter Tight','Inter',sans-serif", fontWeight: 800, fontSize: '1.2rem', color: s?.color, letterSpacing: '-0.02em' }}>{s?.value}</p>
            </div>
          ))}
        </div>

        <div className="space-y-2">
          <Button variant="default" fullWidth onClick={onViewProgress}>
            <Icon name="TrendingUp" size={16} className="mr-2" />
            View Progress
          </Button>
          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" fullWidth onClick={onMessage}>
              <Icon name="MessageCircle" size={16} className="mr-2" />
              Message
            </Button>
            <Button variant="outline" fullWidth onClick={onAssignWorkout}>
              <Icon name="Plus" size={16} className="mr-2" />
              Assign
            </Button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ClientOverviewCard;